// apps/office/src/pages/office/InvoicesList.jsx
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Search, Eye, Download, FileText, Trash2, Mail, Edit,
  TrendingUp, Loader2, CheckCircle, Plus,
} from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import pb from '../../lib/pocketbaseClient';
import { useAuth } from '../../contexts/AuthContext';
import InvoicePDF from '../../components/office/InvoicePDF';
import InteriorInvoicePDF from '../../components/office/InteriorInvoicePDF';
import EmailSendDialog from '../../components/office/EmailSendDialog';

const FIELD_FILTERS = ['all', 'Draft', 'Pending Approval', 'Approved', 'Sent', 'Paid'];

const InvoicesList = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [fieldFilter, setFieldFilter] = useState('all');
  const [emailInvoice, setEmailInvoice] = useState(null);
  const [sendingId, setSendingId] = useState(null);

  const isAdmin = ['admin', 'super_admin'].includes(currentUser?.role);
  const isManager = ['admin', 'super_admin', 'manager'].includes(currentUser?.role);
  const canSendForApproval = ['admin', 'super_admin', 'manager', 'sales'].includes(currentUser?.role);

  console.log('[InvoicesList] Auth check:', {
    currentUser,
    role: currentUser?.role,
    isAdmin,
    isManager,
    canSendForApproval,
  });

  const { data: invoices = [], isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', 'list'],
    queryFn: async () => {
      const records = await pb.collection('invoices').getFullList({
        sort: '-created',
        expand: 'leadId,quotationId,interiorQuotationId',
      });
      console.log('[Invoices] Fetched:', records.length);
      return records;
    },
  });

  const sendForApproval = useMutation({
    mutationFn: async (invoice) => {
      try {
        const admins = await pb.collection('users').getFullList({
          filter: 'role = "admin" || role = "super_admin" || role = "manager"',
        });
        for (const admin of admins) {
          try {
            await pb.collection('notifications').create({
              userId: admin.id,
              type: 'order_placed',
              message: `${currentUser?.name || 'User'} sent invoice ${invoice.invoiceNumber} for approval.`,
              isRead: false,
            });
          } catch {}
        }
      } catch {}

      return pb.collection('invoices').update(invoice.id, {
        field: 'Pending Approval',
      });
    },
    onSuccess: (_, inv) => {
      toast.success(`✅ ${inv.invoiceNumber} sent for approval`);
      queryClient.invalidateQueries(['invoices']);
      refetch();
      setSendingId(null);
    },
    onError: (err) => {
      toast.error('Failed: ' + err.message);
      setSendingId(null);
    },
  });

  const handleSendForApproval = (invoice) => {
    if (!window.confirm(`Send ${invoice.invoiceNumber} for approval?`)) return;
    setSendingId(invoice.id);
    sendForApproval.mutate(invoice);
  };

  /* ─── Load related interior quotations + projects + clients ─── */
  const interiorQuotationIds = invoices
    .filter((inv) => inv.source === 'interior' && inv.interiorQuotationId)
    .map((inv) => inv.interiorQuotationId);

  const { data: interiorData = {} } = useQuery({
    queryKey: ['interior-invoice-data', interiorQuotationIds.join(',')],
    queryFn: async () => {
      if (interiorQuotationIds.length === 0) return {};
      const map = {};
      // Load quotations
      const qs = await pb.collection('interior_quotations').getFullList({
        filter: interiorQuotationIds.map((id) => `id = "${id}"`).join(' || '),
      });
      // Load projects for these quotations
      const projectIds = [...new Set(qs.map((q) => q.projectId).filter(Boolean))];
      let projects = [];
      if (projectIds.length > 0) {
        projects = await pb.collection('interior_projects').getFullList({
          filter: projectIds.map((id) => `id = "${id}"`).join(' || '),
        });
      }
      const projectsById = Object.fromEntries(projects.map((p) => [p.id, p]));
      // Load clients
      const clientIds = [...new Set(projects.map((p) => p.clientId).filter(Boolean))];
      let clients = [];
      if (clientIds.length > 0) {
        clients = await pb.collection('leads').getFullList({
          filter: clientIds.map((id) => `id = "${id}"`).join(' || '),
        });
      }
      const clientsById = Object.fromEntries(clients.map((cl) => [cl.id, cl]));
      // Build map: quotationId → { quotation, project, client }
      qs.forEach((q) => {
        const proj = projectsById[q.projectId] || null;
        const cl = proj ? (clientsById[proj.clientId] || null) : null;
        map[q.id] = { quotation: q, project: proj, client: cl };
      });
      return map;
    },
    enabled: interiorQuotationIds.length > 0,
  });

  const deleteInvoice = useMutation({
    mutationFn: (id) => pb.collection('invoices').delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['invoices']);
      toast.success('Invoice deleted');
      refetch();
    },
    onError: (err) => toast.error('Delete failed: ' + err.message),
  });

  const interiorClientInfo = (inv) => {
    if (inv.source === 'interior' && inv.interiorQuotationId) {
      const data = interiorData[inv.interiorQuotationId];
      return data?.client || null;
    }
    return null;
  };

  const getClientName = (inv) => {
    const ic = interiorClientInfo(inv);
    if (ic) return ic.name || '—';
    return inv.expand?.leadId?.name || inv.expand?.quotationId?.clientName || '—';
  };
  const getClientPhone = (inv) => {
    const ic = interiorClientInfo(inv);
    if (ic) return ic.phone || '';
    return inv.expand?.leadId?.phone || inv.expand?.quotationId?.clientPhone || '';
  };
  const getClientEmail = (inv) => {
    const ic = interiorClientInfo(inv);
    if (ic) return ic.email || '';
    return inv.expand?.leadId?.email || inv.expand?.quotationId?.clientEmail || '';
  };
  const getClientAddress = (inv) => {
    const ic = interiorClientInfo(inv);
    if (ic) return ic.address || '';
    return inv.expand?.leadId?.address || inv.expand?.quotationId?.clientAddress || '';
  };
  const getClientJobId = (inv) => {
    const ic = interiorClientInfo(inv);
    if (ic) return ic.jobId || '';
    return inv.expand?.leadId?.jobId || '';
  };
  const getQuotationNumber = (inv) => {
    if (inv.source === 'interior' && inv.interiorQuotationId) {
      const data = interiorData[inv.interiorQuotationId];
      return data?.quotation?.quoteNumber || '—';
    }
    return inv.expand?.quotationId?.quoteNumber || '—';
  };
  const getItems = (inv) => {
    if (inv.source === 'interior' && inv.interiorQuotationId) {
      const data = interiorData[inv.interiorQuotationId];
      return data?.quotation?.items || [];
    }
    return inv.expand?.quotationId?.items || [];
  };

  const filteredInvoices = invoices.filter((inv) => {
    const name = getClientName(inv);
    const matchesSearch =
      inv.invoiceNumber?.toLowerCase().includes(search.toLowerCase()) ||
      name.toLowerCase().includes(search.toLowerCase()) ||
      getQuotationNumber(inv).toLowerCase().includes(search.toLowerCase());
    const matchesField = fieldFilter === 'all' || (inv.field || 'Draft') === fieldFilter;
    return matchesSearch && matchesField;
  });

  const getStatusBadge = (field) => {
    const colors = {
      Draft: 'bg-gray-100 text-gray-700',
      'Pending Approval': 'bg-yellow-100 text-yellow-700',
      Approved: 'bg-green-100 text-green-700',
      Sent: 'bg-blue-100 text-blue-700',
      Paid: 'bg-emerald-100 text-emerald-700',
    };
    return <Badge className={colors[field] || colors.Draft}>{field || 'Draft'}</Badge>;
  };

  const formatPrice = (p) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency', currency: 'INR', maximumFractionDigits: 0,
    }).format(p || 0);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
    }) : '—';

  const buildPdfElement = (inv) => {
    /* ─── Interior invoice ─── */
    if (inv.source === 'interior' && inv.interiorQuotationId) {
      const data = interiorData[inv.interiorQuotationId];
      if (data) {
        return (
          <InteriorInvoicePDF
            quotation={data.quotation}
            project={data.project}
            client={data.client}
            invoiceNumber={inv.invoiceNumber}
          />
        );
      }
      return (
        <InteriorInvoicePDF
          quotation={null}
          project={null}
          client={null}
          invoiceNumber={inv.invoiceNumber}
        />
      );
    }

    /* ─── Architecture invoice (existing) ─── */
    const items = getItems(inv);
    const subtotal =
      inv.expand?.quotationId?.subtotal ||
      items.reduce((s, i) => s + (i.amount || 0), 0);
    return (
      <InvoicePDF
        invoice={{
          ...inv,
          clientName: getClientName(inv),
          clientPhone: getClientPhone(inv),
          clientEmail: getClientEmail(inv),
          clientAddress: getClientAddress(inv),
          clientJobIdText: getClientJobId(inv),
          quotationNumber: getQuotationNumber(inv),
          total: inv.totalAmount || 0,
        }}
        items={items}
        subtotal={subtotal}
        discountAmount={inv.discountAmount || 0}
        taxAmount={inv.taxAmount || 0}
        total={inv.totalAmount || 0}
        clientJobIdText={getClientJobId(inv)}
      />
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-red-500 font-medium">Failed to load invoices</p>
        <p className="text-sm text-slate-500 mt-2">{error.message}</p>
        <Button variant="outline" className="mt-4" onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-green-600 via-emerald-600 to-teal-500 p-8 shadow-xl">
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <FileText className="w-8 h-8" /> Invoices
            </h1>
            <p className="text-white/80">Invoices generated from approved quotations</p>
          </div>
          <Button
            onClick={() => navigate('/office/invoices/new')}
            className="bg-white text-green-600 hover:bg-green-50 shadow-lg gap-2"
          >
            <Plus className="w-4 h-4" /> Create Invoice
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total</p><p className="text-2xl font-bold">{invoices.length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Pending Approval</p><p className="text-2xl font-bold text-yellow-600">{invoices.filter((i) => (i.field || 'Draft') === 'Pending Approval').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Approved</p><p className="text-2xl font-bold text-green-600">{invoices.filter((i) => i.field === 'Approved').length}</p></CardContent></Card>
        <Card><CardContent className="p-4"><p className="text-xs text-slate-500">Total Value</p><p className="text-2xl font-bold">{formatPrice(invoices.reduce((s, i) => s + (i.totalAmount || 0), 0))}</p></CardContent></Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-2 flex-wrap">
          {FIELD_FILTERS.map((f) => (
            <Button key={f} variant={fieldFilter === f ? 'default' : 'outline'} size="sm" onClick={() => setFieldFilter(f)} className="capitalize">{f}</Button>
          ))}
        </div>
      </div>

      {/* Table */}
      <Card className="border-0 shadow-xl overflow-hidden">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader className="bg-slate-50">
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>From Quotation</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInvoices.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No invoices yet.</TableCell></TableRow>
              ) : (
                filteredInvoices.map((invoice) => {
                  const isSending = sendingId === invoice.id;
                  const field = invoice.field || 'Draft';
                  const isDraft = field === 'Draft';
                  const canSend = canSendForApproval && isDraft;
                  const canEdit = isAdmin && (field === 'Draft' || field === 'Pending Approval');

                  return (
                    <TableRow key={invoice.id} className="hover:bg-green-50/50">
                      <TableCell className="font-mono font-medium">{invoice.invoiceNumber || '—'}</TableCell>
                      <TableCell className="font-mono text-xs text-indigo-600">{getQuotationNumber(invoice)}</TableCell>
                      <TableCell>
                        <p className="font-medium">{getClientName(invoice)}</p>
                        <p className="text-xs text-slate-500">{getClientPhone(invoice)}</p>
                      </TableCell>
                      <TableCell>{formatDate(invoice.invoiceDate || invoice.created)}</TableCell>
                      <TableCell className="font-semibold">{formatPrice(invoice.totalAmount)}</TableCell>
                      <TableCell>{getStatusBadge(field)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {/* View */}
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/office/invoices/${invoice.id}`)} title="View">
                            <Eye className="w-4 h-4" />
                          </Button>

                          {/* ⭐ Edit (admin only, Draft/Pending) */}
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => navigate(`/office/invoices/edit/${invoice.id}`)}
                              title="Edit"
                              className="text-amber-600 hover:text-amber-800"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          {/* PDF */}
                          <PDFDownloadLink document={buildPdfElement(invoice)} fileName={`invoice_${invoice.invoiceNumber}.pdf`}>
                            {({ loading }) => (
                              <Button variant="ghost" size="sm" disabled={loading} title="Download PDF">
                                <Download className="w-4 h-4" />
                              </Button>
                            )}
                          </PDFDownloadLink>

                          {/* Email */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setEmailInvoice(invoice)}
                            title="Email"
                            className="text-emerald-600 hover:text-emerald-800"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>

                          {/* ⭐ Send for Approval (Draft only) */}
                          {canSend && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleSendForApproval(invoice)}
                              disabled={isSending}
                              title="Send for Approval"
                              className="text-yellow-700 hover:text-yellow-900 bg-yellow-50 hover:bg-yellow-100 border border-yellow-200"
                            >
                              {isSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
                            </Button>
                          )}

                          {field === 'Approved' && (
                            <span title="Approved" className="inline-flex items-center px-1">
                              <CheckCircle className="w-4 h-4 text-green-600" />
                            </span>
                          )}

                          {/* Delete */}
                          {isAdmin && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                if (window.confirm('Delete this invoice?')) deleteInvoice.mutate(invoice.id);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {emailInvoice && (
        <EmailSendDialog
          open={!!emailInvoice}
          onOpenChange={(v) => !v && setEmailInvoice(null)}
          documentType="invoice"
          document={{
            ...emailInvoice,
            clientName: getClientName(emailInvoice),
            clientEmail: getClientEmail(emailInvoice),
          }}
          pdfElement={buildPdfElement(emailInvoice)}
        />
      )}
    </div>
  );
};

export default InvoicesList;
